import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABEL = { required_conditions: "Required", preferences: "Preference", exclusions: "Exclusion", things_to_verify: "Verify in conversation", business_types: "Business type", geography: "Geography" };

/** AI-proposed ICP refinements learned from accepted/rejected candidates. Nothing changes until the user approves each one. */
export function ICPSuggestions({ icp, onChanged, compact = false }) {
  const [busy, setBusy] = useState(false);
  const pending = icp?.pending_suggestions || [];

  const suggest = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/icps/${icp.id}/suggest`); toast.success(data.suggestions.length ? `${data.suggestions.length} refinement${data.suggestions.length === 1 ? "" : "s"} to review` : "No changes suggested - the ICP looks consistent with your decisions"); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const decide = async (s, accept) => {
    try { await api.post(`/icps/${icp.id}/suggestions/decide`, { suggestion_id: s.id, accept }); toast.success(accept ? "Applied to the ICP" : "Dismissed"); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!icp) return null;
  return (
    <div className="space-y-2" data-testid="icp-suggestions">
      {pending.length > 0 && (
        <div className="rounded-md border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning-bg))] p-2.5 space-y-2">
          <div className="text-xs font-medium flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" />AI refinements from your accept/reject decisions - nothing changes until you approve</div>
          {pending.map((s) => (
            <div key={s.id} className="flex items-start gap-2 text-xs bg-card rounded border border-border p-2" data-testid="icp-suggestion">
              <div className="flex-1 min-w-0">
                <div><span className={`chip ${s.action === "add" ? "chip-success" : "chip-danger"} mr-1`}>{s.action === "add" ? "Add" : "Remove"}</span><span className="chip chip-muted mr-1">{FIELD_LABEL[s.field] || s.field}</span><span className="font-medium">{s.value}</span></div>
                <div className="text-muted-foreground mt-1">{s.reason}</div>
              </div>
              {icp.can_edit && <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => decide(s, true)} data-testid="icp-suggestion-accept" aria-label="apply"><Check className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => decide(s, false)} data-testid="icp-suggestion-reject" aria-label="dismiss"><X className="h-3.5 w-3.5" /></Button>
              </div>}
            </div>
          ))}
        </div>
      )}
      <Button size="sm" variant={compact ? "ghost" : "outline"} className={compact ? "h-7 px-2 text-xs" : ""} onClick={suggest} disabled={busy} data-testid="icp-suggest-button"><Sparkles className="h-3.5 w-3.5 mr-1" />{busy ? "Learning from decisions…" : pending.length ? "Refresh suggestions" : "Suggest refinements from results"}</Button>
    </div>
  );
}
